import React, { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, XCircle, RefreshCw, FileText, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const TOKEN = () => localStorage.getItem("token");
const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${TOKEN()}`;
const isImg = (path) => /\.(jpg|jpeg|png|heic|webp)$/i.test(path || "");

const statusTone = (s) => ({ "Pending Review": "text-amber-700 bg-amber-50 border-amber-200", "Approved": "text-green-700 bg-green-50 border-green-200", "Rejected": "text-rose-700 bg-rose-50 border-rose-200", "Replacement Requested": "text-rose-700 bg-rose-50 border-rose-200" }[s] || "text-slate-600 bg-slate-50 border-slate-200");

export default function UploadReview() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [f, setF] = useState({ status: "Pending Review", kind: "all" });
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (f.status && f.status !== "all") params.set("status", f.status);
    if (f.kind && f.kind !== "all") params.set("kind", f.kind);
    api.get(`/admin/upload-review?${params}`).then(({ data }) => setRows(data.items)).catch(() => setRows([]));
    api.get("/admin/upload-review/summary").then(({ data }) => setSummary(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f.status, f.kind]);

  const act = async (item, action) => {
    let reason = null;
    if (action === "reject" || action === "request_replacement") {
      reason = window.prompt(action === "reject" ? "Rejection reason (shown to contractor):" : "What replacement is needed?");
      if (!reason || !reason.trim()) return;
    }
    setBusyId(item.id);
    try {
      const base = item.kind === "document" ? `/admin/upload-review/document/${item.id}/action` : `/admin/upload-review/photo/${item.id}/action`;
      await api.post(base, { action, reason: reason?.trim() });
      toast.success(action === "approve" ? "Approved — eligibility updated" : action === "reject" ? "Rejected" : "Replacement requested");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusyId(null); }
  };

  return (
    <DashboardLayout nav={getNav(user.role)} title="Contractor Upload Review">
      {summary && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="review-summary">
          <Kpi l="Pending Total" v={summary.pending_total} tone="amber" />
          <Kpi l="Pending Documents" v={summary.pending_documents} />
          <Kpi l="Pending After-Photos" v={summary.pending_photos} />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4 bg-white border border-slate-200 rounded-xl p-4">
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="review-status-filter" className="w-48"><SelectValue /></SelectTrigger><SelectContent>{["Pending Review", "Approved", "Rejected", "Replacement Requested", "all"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All Statuses" : s}</SelectItem>)}</SelectContent></Select>
        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}><SelectTrigger data-testid="review-kind-filter" className="w-40"><SelectValue /></SelectTrigger><SelectContent>{[["all", "All Types"], ["document", "Documents"], ["photo", "After Photos"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
        <Button size="sm" variant="outline" data-testid="review-refresh" onClick={load}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
      </div>

      {!rows ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        rows.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-slate-200"><CheckCircle2 className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No uploads to review.</p></div> : (
          <div className="space-y-2" data-testid="review-list">
            {rows.map((item) => (
              <div key={`${item.kind}-${item.id}`} data-testid={`review-row-${item.id}`} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-4 flex-wrap">
                <a href={fileUrl(item.file_path)} target="_blank" rel="noreferrer" className="shrink-0" data-testid={`preview-${item.id}`}>
                  {isImg(item.file_path) ? <img src={fileUrl(item.file_path)} alt="" className="w-20 h-20 object-cover rounded-lg border border-slate-200" /> : <div className="w-20 h-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400"><FileText className="w-8 h-8" /></div>}
                </a>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.kind === "document" ? <FileText className="w-4 h-4 text-slate-400" /> : <ImageIcon className="w-4 h-4 text-slate-400" />}
                    <span className="font-medium text-slate-900">{item.type}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusTone(item.status)}`}>{item.status}</span>
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5">{item.contractor?.name}{item.contractor?.trade ? ` · ${item.contractor.trade}` : ""}{item.work_order ? ` · ${item.work_order.wo_number}` : ""}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{item.eligibility_impact} · Submitted {item.submitted_at ? new Date(item.submitted_at).toLocaleString() : "—"}{item.expiration_date ? ` · Expires ${item.expiration_date}` : ""}{item.doc_number ? ` · #${item.doc_number}` : ""}</div>
                  {item.reject_reason && <div className="text-xs text-rose-600 mt-1">Reason: {item.reject_reason}</div>}
                </div>
                {item.status === "Pending Review" && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" data-testid={`approve-${item.id}`} disabled={busyId === item.id} onClick={() => act(item, "approve")} className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>
                    <Button size="sm" variant="outline" data-testid={`reject-${item.id}`} disabled={busyId === item.id} onClick={() => act(item, "reject")}><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                    {item.kind === "document" && <Button size="sm" variant="outline" data-testid={`replace-${item.id}`} disabled={busyId === item.id} onClick={() => act(item, "request_replacement")}>Request Replacement</Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </DashboardLayout>
  );
}

const Kpi = ({ l, v, tone }) => <div className="bg-white border border-slate-200 rounded-xl p-3"><div className="text-xs text-slate-400">{l}</div><div className={`font-head text-2xl font-semibold ${tone === "amber" ? "text-amber-600" : "text-slate-900"}`}>{v}</div></div>;
